import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import Select from "react-select";
import { debounce } from "lodash";
import { getTrainers, Trainer } from "../../services/trainerService";
import {
  searchExercisesId,
  getExerciseSwaps,
  addExerciseSwap,
  removeExerciseSwap,
} from "../../services/exerciseService";
import {
  updateTrainerProgram,
  createTrainerProgram,
  getTrainerProgram,
  TrainerProgramPayload,
  TrainerWorkoutPayload,
  TrainerWorkoutExercisePayload,
  RecommendedSet,
  TrainerProgram,
} from "../../services/trainerProgramService";
import { uploadFile } from "../../services/exerciseService";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const TRACK_TYPES = [
  { value: "strength", label: "Strength" },
  { value: "strength_conditioning", label: "Strength Conditioning" },
  { value: "home", label: "Home" },
] as const;

const BLOCK_TYPES: { value: BlockTypeKey; label: string }[] = [
  { value: "warmup", label: "Warm-Up" },
  { value: "main", label: "Main" },
  { value: "finisher", label: "Finisher" },
  { value: "cooldown", label: "Cool-Down" },
];

type DayKey = (typeof DAYS)[number];
type TrackTypeKey = "strength" | "strength_conditioning" | "home";
type BlockTypeKey = "warmup" | "main" | "finisher" | "cooldown";

interface ExerciseOption {
  value: number;
  label: string;
  exerciseType?: string;
}

const SET_FIELDS: Record<
  string,
  {
    key: "reps" | "kg" | "time" | "meters" | "minutes" | "kilometers";
    label: string;
  }[]
> = {
  weight_and_reps_based: [
    { key: "reps", label: "Reps" },
    { key: "kg", label: "Kg" },
  ],
  time_based: [{ key: "time", label: "Sec" }],
  distance_based: [{ key: "meters", label: "Km" }],
  cardio: [
    { key: "minutes", label: "Min" },
    { key: "kilometers", label: "Km" },
  ],
};

const getSetFields = (exerciseType?: string) =>
  SET_FIELDS[exerciseType ?? ""] ?? [
    { key: "reps" as const, label: "Reps" },
    { key: "kg" as const, label: "Kg" },
  ];

interface WorkoutExerciseForm {
  exerciseId: number | null;
  exerciseName: string;
  exerciseType: string;
  restTimeSec: string;
  notes: string;
  sets: {
    reps: string;
    kg: string;
    time: string;
    meters: string;
    minutes: string;
    kilometers: string;
  }[];
  swaps: { id: number; name: string }[];
}

interface BlockForm {
  exercises: WorkoutExerciseForm[];
}

interface TrackForm {
  enabled: boolean;
  name: string;
  description: string;
  duration: string;
  blocks: Record<BlockTypeKey, BlockForm>;
}

interface DayForm {
  isRestDay: boolean;
  restDayMessage: string;
  tracks: Record<TrackTypeKey, TrackForm>;
}

const defaultExercise = (): WorkoutExerciseForm => ({
  exerciseId: null,
  exerciseName: "",
  exerciseType: "",
  restTimeSec: "60",
  notes: "",
  sets: [
    { reps: "", kg: "", time: "", meters: "", minutes: "", kilometers: "" },
  ],
  swaps: [],
});

const defaultBlock = (): BlockForm => ({ exercises: [] });

const defaultTrack = (): TrackForm => ({
  enabled: false,
  name: "",
  description: "",
  duration: "",
  blocks: {
    warmup: defaultBlock(),
    main: defaultBlock(),
    finisher: defaultBlock(),
    cooldown: defaultBlock(),
  },
});

const defaultDay = (): DayForm => ({
  isRestDay: false,
  restDayMessage: "",
  tracks: {
    strength: defaultTrack(),
    strength_conditioning: defaultTrack(),
    home: defaultTrack(),
  },
});

// Build form state from existing program data
const programToDays = (program: TrainerProgram): Record<DayKey, DayForm> => {
  const result = Object.fromEntries(
    DAYS.map((d) => [d, defaultDay()]),
  ) as Record<DayKey, DayForm>;

  for (const workout of program.workouts) {
    const day = workout.dayOfWeek as DayKey;
    if (!day || !DAYS.includes(day)) continue;

    if (workout.isRestDay) {
      result[day].isRestDay = true;
      result[day].restDayMessage = workout.restDayMessage ?? "";
    } else if (workout.trackType) {
      const track = workout.trackType as TrackTypeKey;
      const block = (workout.blockType ?? "main") as BlockTypeKey;

      if (!result[day].tracks[track].enabled) {
        result[day].tracks[track].enabled = true;
        result[day].tracks[track].name = workout.name;
        result[day].tracks[track].description = workout.description ?? "";
        result[day].tracks[track].duration = workout.duration
          ? String(workout.duration)
          : "";
      }

      const exercises: WorkoutExerciseForm[] = workout.exercises.map((ex) => ({
        exerciseId: ex.exercise.id,
        exerciseName: ex.exercise.name,
        exerciseType: ex.exercise.exerciseType ?? "",
        restTimeSec: ex.restTimeSec ? String(ex.restTimeSec) : "60",
        notes: ex.notes ?? "",
        sets: (ex.recommendedSets.length > 0
          ? ex.recommendedSets.map((s) => ({
              reps: s.reps ? String(s.reps) : "",
              kg: s.kg ? String(s.kg) : "",
              time: s.time ? String(s.time) : "",
              meters: s.meters ? String(s.meters / 1000) : "",
              minutes: s.minutes ? String(s.minutes) : "",
              kilometers: s.kilometers ? String(s.kilometers) : "",
            }))
          : [
              {
                reps: "",
                kg: "",
                time: "",
                meters: "",
                minutes: "",
                kilometers: "",
              },
            ]) as WorkoutExerciseForm["sets"],
        swaps:
          ex.exercise.swapOptions?.map((s) => ({ id: s.id, name: s.name })) ??
          [],
      }));

      result[day].tracks[track].blocks[block].exercises = [
        ...result[day].tracks[track].blocks[block].exercises,
        ...exercises,
      ];
    }
  }

  return result;
};

const EditTrainerProgramPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const program = location.state as TrainerProgram | null;

  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [title, setTitle] = useState(program?.title ?? "");
  const [description, setDescription] = useState(program?.description ?? "");
  const [bannerUrl, setBannerUrl] = useState(program?.bannerUrl ?? "");
  const [trainerId, setTrainerId] = useState<number | "">(
    program?.trainer?.id ?? "",
  );
  const [days, setDays] = useState<Record<DayKey, DayForm>>(() =>
    program
      ? programToDays(program)
      : (Object.fromEntries(DAYS.map((d) => [d, defaultDay()])) as Record<
          DayKey,
          DayForm
        >),
  );
  const [activeDay, setActiveDay] = useState<DayKey>("monday");
  const [exerciseOptions, setExerciseOptions] = useState<ExerciseOption[]>([]);
  const [swapOptions, setSwapOptions] = useState<ExerciseOption[]>([]);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!program) {
      navigate("/trainer-programs");
      return;
    }
    getTrainers()
      .then(setTrainers)
      .catch(() => {});
    getTrainerProgram(program.id)
      .then((full) => setDays(programToDays(full)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchExercises = React.useRef(
    debounce(async (query: string) => {
      if (!query.trim()) return;
      const results = await searchExercisesId("name", query);
      setExerciseOptions(
        results.map((e) => ({
          value: e.id,
          label: e.name,
          exerciseType: e.exerciseType,
        })),
      );
    }, 400),
  ).current;

  const searchSwapExercises = React.useRef(
    debounce(async (query: string) => {
      if (!query.trim()) return;
      const results = await searchExercisesId("name", query);
      setSwapOptions(results.map((e) => ({ value: e.id, label: e.name })));
    }, 400),
  ).current;

  const handleBannerUpload = async (file: File) => {
    setBannerUploading(true);
    try {
      const { url } = await uploadFile(file);
      setBannerUrl(url);
    } catch {
      setError("Failed to upload banner");
    } finally {
      setBannerUploading(false);
    }
  };

  // --- Day helpers ---
  const setDayField = (day: DayKey, field: keyof DayForm, value: unknown) => {
    setDays((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const setTrackField = (
    day: DayKey,
    track: TrackTypeKey,
    field: keyof TrackForm,
    value: unknown,
  ) => {
    setDays((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        tracks: {
          ...prev[day].tracks,
          [track]: { ...prev[day].tracks[track], [field]: value },
        },
      },
    }));
  };

  const addExercise = (
    day: DayKey,
    track: TrackTypeKey,
    block: BlockTypeKey,
  ) => {
    setDays((prev) => {
      const t = prev[day].tracks[track];
      return {
        ...prev,
        [day]: {
          ...prev[day],
          tracks: {
            ...prev[day].tracks,
            [track]: {
              ...t,
              blocks: {
                ...t.blocks,
                [block]: {
                  exercises: [...t.blocks[block].exercises, defaultExercise()],
                },
              },
            },
          },
        },
      };
    });
  };

  const removeExercise = (
    day: DayKey,
    track: TrackTypeKey,
    block: BlockTypeKey,
    idx: number,
  ) => {
    setDays((prev) => {
      const t = prev[day].tracks[track];
      return {
        ...prev,
        [day]: {
          ...prev[day],
          tracks: {
            ...prev[day].tracks,
            [track]: {
              ...t,
              blocks: {
                ...t.blocks,
                [block]: {
                  exercises: t.blocks[block].exercises.filter(
                    (_, i) => i !== idx,
                  ),
                },
              },
            },
          },
        },
      };
    });
  };

  const setExerciseField = (
    day: DayKey,
    track: TrackTypeKey,
    block: BlockTypeKey,
    idx: number,
    field: keyof WorkoutExerciseForm,
    value: unknown,
  ) => {
    setDays((prev) => {
      const t = prev[day].tracks[track];
      const exercises = [...t.blocks[block].exercises];
      exercises[idx] = { ...exercises[idx], [field]: value };
      return {
        ...prev,
        [day]: {
          ...prev[day],
          tracks: {
            ...prev[day].tracks,
            [track]: { ...t, blocks: { ...t.blocks, [block]: { exercises } } },
          },
        },
      };
    });
  };

  const addSet = (
    day: DayKey,
    track: TrackTypeKey,
    block: BlockTypeKey,
    exIdx: number,
  ) => {
    setDays((prev) => {
      const t = prev[day].tracks[track];
      const exercises = [...t.blocks[block].exercises];
      exercises[exIdx] = {
        ...exercises[exIdx],
        sets: [
          ...exercises[exIdx].sets,
          {
            reps: "",
            kg: "",
            time: "",
            meters: "",
            minutes: "",
            kilometers: "",
          },
        ],
      };
      return {
        ...prev,
        [day]: {
          ...prev[day],
          tracks: {
            ...prev[day].tracks,
            [track]: { ...t, blocks: { ...t.blocks, [block]: { exercises } } },
          },
        },
      };
    });
  };

  const removeSet = (
    day: DayKey,
    track: TrackTypeKey,
    block: BlockTypeKey,
    exIdx: number,
    setIdx: number,
  ) => {
    setDays((prev) => {
      const t = prev[day].tracks[track];
      const exercises = [...t.blocks[block].exercises];
      exercises[exIdx] = {
        ...exercises[exIdx],
        sets: exercises[exIdx].sets.filter((_, i) => i !== setIdx),
      };
      return {
        ...prev,
        [day]: {
          ...prev[day],
          tracks: {
            ...prev[day].tracks,
            [track]: { ...t, blocks: { ...t.blocks, [block]: { exercises } } },
          },
        },
      };
    });
  };

  const setSetField = (
    day: DayKey,
    track: TrackTypeKey,
    block: BlockTypeKey,
    exIdx: number,
    setIdx: number,
    field: "reps" | "kg" | "time" | "meters" | "minutes" | "kilometers",
    value: string,
  ) => {
    setDays((prev) => {
      const t = prev[day].tracks[track];
      const exercises = [...t.blocks[block].exercises];
      const sets = [...exercises[exIdx].sets];
      sets[setIdx] = { ...sets[setIdx], [field]: value };
      exercises[exIdx] = { ...exercises[exIdx], sets };
      return {
        ...prev,
        [day]: {
          ...prev[day],
          tracks: {
            ...prev[day].tracks,
            [track]: { ...t, blocks: { ...t.blocks, [block]: { exercises } } },
          },
        },
      };
    });
  };

  const handleAddSwap = async (
    day: DayKey,
    track: TrackTypeKey,
    block: BlockTypeKey,
    exIdx: number,
    swapId: number,
    swapName: string,
  ) => {
    const ex = days[day].tracks[track].blocks[block].exercises[exIdx];
    if (!ex.exerciseId || ex.swaps.find((s) => s.id === swapId)) return;
    await addExerciseSwap(ex.exerciseId, swapId);
    setExerciseField(day, track, block, exIdx, "swaps", [
      ...ex.swaps,
      { id: swapId, name: swapName },
    ]);
  };

  const handleRemoveSwap = async (
    day: DayKey,
    track: TrackTypeKey,
    block: BlockTypeKey,
    exIdx: number,
    swapId: number,
  ) => {
    const ex = days[day].tracks[track].blocks[block].exercises[exIdx];
    if (!ex.exerciseId) return;
    await removeExerciseSwap(ex.exerciseId, swapId);
    setExerciseField(
      day,
      track,
      block,
      exIdx,
      "swaps",
      ex.swaps.filter((s) => s.id !== swapId),
    );
  };

  // --- Build payload ---
  const buildExercises = (
    exercises: WorkoutExerciseForm[],
  ): TrainerWorkoutExercisePayload[] =>
    exercises
      .filter((e) => e.exerciseId !== null)
      .map((e, i) => ({
        exerciseId: e.exerciseId!,
        position: i,
        restTimeSec: e.restTimeSec ? Number(e.restTimeSec) : undefined,
        notes: e.notes || undefined,
        recommendedSets: e.sets
          .filter((s) => Object.values(s).some((v) => v !== ""))
          .map((s, si) => {
            const rs: RecommendedSet = { set: si + 1 };
            if (s.reps) rs.reps = Number(s.reps);
            if (s.kg) rs.kg = Number(s.kg);
            if (s.time) rs.time = Number(s.time);
            if (s.meters) rs.meters = Math.round(Number(s.meters) * 1000);
            if (s.minutes) rs.minutes = Number(s.minutes);
            if (s.kilometers) rs.kilometers = Number(s.kilometers);
            return rs;
          }),
      }));

  const buildPayload = (): TrainerProgramPayload => {
    const workouts: TrainerWorkoutPayload[] = [];
    let position = 0;

    DAYS.forEach((day) => {
      const dayForm = days[day];
      if (dayForm.isRestDay) {
        workouts.push({
          name: `${day.charAt(0).toUpperCase() + day.slice(1)} Rest`,
          dayOfWeek: day,
          isRestDay: true,
          restDayMessage: dayForm.restDayMessage,
          position: position++,
        });
      } else {
        (Object.keys(dayForm.tracks) as TrackTypeKey[]).forEach((track) => {
          const t = dayForm.tracks[track];
          if (!t.enabled) return;

          BLOCK_TYPES.forEach(({ value: block, label: blockLabel }) => {
            const blockExercises = buildExercises(t.blocks[block].exercises);
            if (blockExercises.length === 0) return;

            workouts.push({
              name:
                t.name ||
                `${day.charAt(0).toUpperCase() + day.slice(1)} ${track} - ${blockLabel}`,
              description: t.description || undefined,
              dayOfWeek: day,
              trackType: track,
              blockType: block,
              isRestDay: false,
              duration: t.duration ? Number(t.duration) : undefined,
              position: position++,
              exercises: blockExercises,
            });
          });
        });
      }
    });

    return {
      title,
      description,
      bannerUrl: bannerUrl || undefined,
      trainerId: trainerId || undefined,
      isActive: program?.isActive ?? false,
      workouts,
    };
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!program) return;
    setSubmitting(true);
    try {
      await updateTrainerProgram(program.id, buildPayload());
      setSuccess("Program updated successfully!");
      setTimeout(() => navigate("/trainer-programs"), 1500);
    } catch {
      setError("Failed to update program");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAsNew = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      await createTrainerProgram({ ...buildPayload(), isActive: false, trainerId: undefined });
      setSuccess("Saved as new program!");
      setTimeout(() => navigate("/trainer-programs"), 1500);
    } catch {
      setError("Failed to save as new program");
    } finally {
      setSubmitting(false);
    }
  };

  if (!program) return null;

  const dayForm = days[activeDay];

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <BackButton onClick={() => navigate("/trainer-programs")} />
      <Header title={`Edit: ${program.title}`} />

      {success && (
        <SuccessMessage message={success} onClose={() => setSuccess(null)} />
      )}
      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}

      {/* Program Info */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Program Info</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Trainer</label>
            <select
              value={trainerId}
              onChange={(e) =>
                setTrainerId(e.target.value ? Number(e.target.value) : "")
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select trainer</option>
              {trainers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Banner</label>
            <div className="flex items-center gap-3">
              {bannerUrl && (
                <span className="text-sm text-gray-500 truncate max-w-xs">
                  Current: {bannerUrl.split("/").pop()}
                </span>
              )}
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) =>
                  e.target.files?.[0] && handleBannerUpload(e.target.files[0])
                }
                className="border border-gray-300 rounded-md px-3 py-2"
              />
              {bannerUploading && (
                <span className="text-sm text-gray-500">Uploading...</span>
              )}
              {bannerUrl && !bannerUploading && (
                <span className="text-sm text-green-600">✓ Uploaded</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Schedule */}
      <div className="bg-white rounded-lg shadow-sm">
        {/* Day tabs */}
        <div className="flex border-b overflow-x-auto">
          {DAYS.map((day) => {
            const hasContent =
              days[day].isRestDay ||
              Object.values(days[day].tracks).some((t) => t.enabled);
            return (
              <button
                key={day}
                onClick={() => setActiveDay(day)}
                className={`px-4 py-3 text-sm font-medium capitalize whitespace-nowrap transition-colors ${
                  activeDay === day
                    ? "border-b-2 border-yellow-600 text-yellow-700"
                    : "text-gray-500 hover:text-gray-700"
                } ${hasContent ? "font-semibold" : ""}`}
              >
                {day.slice(0, 3).toUpperCase()}
                {hasContent && (
                  <span className="ml-1 w-2 h-2 inline-block rounded-full bg-yellow-500" />
                )}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {/* Rest day toggle */}
          <div className="flex items-center gap-3 mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={dayForm.isRestDay}
                onChange={(e) =>
                  setDayField(activeDay, "isRestDay", e.target.checked)
                }
                className="w-4 h-4"
              />
              <span className="font-medium">Rest Day</span>
            </label>
            {dayForm.isRestDay && (
              <input
                type="text"
                value={dayForm.restDayMessage}
                onChange={(e) =>
                  setDayField(activeDay, "restDayMessage", e.target.value)
                }
                placeholder="Rest day message (e.g. Stretching, Walking, Meditation)"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            )}
          </div>

          {/* Track sections */}
          {!dayForm.isRestDay && (
            <div className="space-y-6">
              {TRACK_TYPES.map(({ value: track, label }) => {
                const trackForm = dayForm.tracks[track];
                return (
                  <div
                    key={track}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Track header */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50">
                      <input
                        type="checkbox"
                        checked={trackForm.enabled}
                        onChange={(e) =>
                          setTrackField(
                            activeDay,
                            track,
                            "enabled",
                            e.target.checked,
                          )
                        }
                        className="w-4 h-4"
                      />
                      <span className="font-semibold text-gray-700">
                        {label}
                      </span>
                    </div>

                    {trackForm.enabled && (
                      <div className="p-4 space-y-4">
                        {/* Workout meta */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">
                              Workout Name
                            </label>
                            <input
                              type="text"
                              value={trackForm.name}
                              onChange={(e) =>
                                setTrackField(
                                  activeDay,
                                  track,
                                  "name",
                                  e.target.value,
                                )
                              }
                              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                              placeholder={`${label} workout`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">
                              Duration (min)
                            </label>
                            <input
                              type="number"
                              value={trackForm.duration}
                              onChange={(e) =>
                                setTrackField(
                                  activeDay,
                                  track,
                                  "duration",
                                  e.target.value,
                                )
                              }
                              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                              placeholder="45"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs text-gray-500 mb-1 block">
                              Description
                            </label>
                            <textarea
                              value={trackForm.description}
                              onChange={(e) =>
                                setTrackField(
                                  activeDay,
                                  track,
                                  "description",
                                  e.target.value,
                                )
                              }
                              rows={2}
                              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm resize-none"
                              placeholder="Workout description..."
                            />
                          </div>
                        </div>

                        {/* Blocks */}
                        <div className="space-y-4">
                          {BLOCK_TYPES.map(
                            ({ value: block, label: blockLabel }) => {
                              const blockForm = trackForm.blocks[block];
                              return (
                                <div
                                  key={block}
                                  className="border border-gray-100 rounded-lg overflow-hidden"
                                >
                                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
                                    <span className="text-sm font-semibold text-gray-600">
                                      {blockLabel}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      {blockForm.exercises.length} exercise
                                      {blockForm.exercises.length !== 1
                                        ? "s"
                                        : ""}
                                    </span>
                                  </div>

                                  <div className="p-3 space-y-3">
                                    {blockForm.exercises.map((ex, exIdx) => (
                                      <div
                                        key={exIdx}
                                        className="border border-gray-100 rounded-lg p-3 bg-white"
                                      >
                                        <div className="flex items-start gap-3 mb-2">
                                          <div className="flex-1">
                                            <label className="text-xs text-gray-500 mb-1 block">
                                              Exercise
                                            </label>
                                            <Select<ExerciseOption>
                                              options={exerciseOptions}
                                              value={
                                                ex.exerciseId
                                                  ? {
                                                      value: ex.exerciseId,
                                                      label: ex.exerciseName,
                                                    }
                                                  : null
                                              }
                                              onChange={(opt) => {
                                                if (!opt) return;
                                                setExerciseField(
                                                  activeDay,
                                                  track,
                                                  block,
                                                  exIdx,
                                                  "exerciseId",
                                                  opt.value,
                                                );
                                                setExerciseField(
                                                  activeDay,
                                                  track,
                                                  block,
                                                  exIdx,
                                                  "exerciseName",
                                                  opt.label,
                                                );
                                                setExerciseField(
                                                  activeDay,
                                                  track,
                                                  block,
                                                  exIdx,
                                                  "exerciseType",
                                                  opt.exerciseType ?? "",
                                                );
                                                getExerciseSwaps(opt.value)
                                                  .then((list) =>
                                                    setExerciseField(
                                                      activeDay,
                                                      track,
                                                      block,
                                                      exIdx,
                                                      "swaps",
                                                      list.map((s) => ({
                                                        id: s.id,
                                                        name: s.name,
                                                      })),
                                                    ),
                                                  )
                                                  .catch(() => {});
                                              }}
                                              onInputChange={(val) => {
                                                searchExercises(val);
                                              }}
                                              placeholder="Search exercise..."
                                              className="text-sm"
                                            />
                                          </div>
                                          <div className="w-28">
                                            <label className="text-xs text-gray-500 mb-1 block">
                                              Rest (sec)
                                            </label>
                                            <input
                                              type="number"
                                              value={ex.restTimeSec}
                                              onChange={(e) =>
                                                setExerciseField(
                                                  activeDay,
                                                  track,
                                                  block,
                                                  exIdx,
                                                  "restTimeSec",
                                                  e.target.value,
                                                )
                                              }
                                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                                              placeholder="60"
                                            />
                                          </div>
                                          <button
                                            onClick={() =>
                                              removeExercise(
                                                activeDay,
                                                track,
                                                block,
                                                exIdx,
                                              )
                                            }
                                            className="mt-5 text-red-500 hover:text-red-700 text-sm"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                        <div className="mb-2">
                                          <label className="text-xs text-gray-500 mb-1 block">
                                            Notes
                                          </label>
                                          <textarea
                                            value={ex.notes}
                                            onChange={(e) =>
                                              setExerciseField(
                                                activeDay,
                                                track,
                                                block,
                                                exIdx,
                                                "notes",
                                                e.target.value,
                                              )
                                            }
                                            rows={2}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-none"
                                            placeholder="Trainer notes for this exercise..."
                                          />
                                        </div>

                                        {/* Sets */}
                                        <div className="mt-2">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs font-medium text-gray-600">
                                              Sets
                                            </span>
                                            <button
                                              onClick={() =>
                                                addSet(
                                                  activeDay,
                                                  track,
                                                  block,
                                                  exIdx,
                                                )
                                              }
                                              className="text-xs text-yellow-600 hover:underline"
                                            >
                                              + Add set
                                            </button>
                                          </div>
                                          <div className="space-y-1">
                                            {ex.sets.map((set, si) => (
                                              <div
                                                key={si}
                                                className="flex items-center gap-2"
                                              >
                                                <span className="text-xs text-gray-400 w-8">
                                                  #{si + 1}
                                                </span>
                                                {getSetFields(
                                                  ex.exerciseType,
                                                ).map(
                                                  ({
                                                    key,
                                                    label: fieldLabel,
                                                  }) => (
                                                    <input
                                                      key={key}
                                                      type="number"
                                                      value={set[key]}
                                                      onChange={(e) =>
                                                        setSetField(
                                                          activeDay,
                                                          track,
                                                          block,
                                                          exIdx,
                                                          si,
                                                          key,
                                                          e.target.value,
                                                        )
                                                      }
                                                      className={`w-20 border rounded px-2 py-1 text-xs ${set[key] === "" ? "border-red-300 bg-red-50" : "border-gray-300"}`}
                                                      placeholder={fieldLabel}
                                                    />
                                                  ),
                                                )}
                                                {ex.sets.length > 1 && (
                                                  <button
                                                    onClick={() =>
                                                      removeSet(
                                                        activeDay,
                                                        track,
                                                        block,
                                                        exIdx,
                                                        si,
                                                      )
                                                    }
                                                    className="text-red-400 hover:text-red-600 text-xs px-1"
                                                  >
                                                    ✕
                                                  </button>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>

                                        {/* Swap options */}
                                        {ex.exerciseId && (
                                          <div className="mt-2 pt-2 border-t border-gray-100">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="text-xs font-medium text-gray-600">
                                                Swap Options
                                              </span>
                                            </div>
                                            {ex.swaps.length > 0 && (
                                              <div className="flex flex-wrap gap-1 mb-2">
                                                {ex.swaps.map((swap) => (
                                                  <span
                                                    key={swap.id}
                                                    className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-0.5 text-xs text-gray-700"
                                                  >
                                                    {swap.name}
                                                    <button
                                                      onClick={() =>
                                                        handleRemoveSwap(
                                                          activeDay,
                                                          track,
                                                          block,
                                                          exIdx,
                                                          swap.id,
                                                        )
                                                      }
                                                      className="text-red-400 hover:text-red-600"
                                                    >
                                                      ✕
                                                    </button>
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                            <Select<ExerciseOption>
                                              options={swapOptions.filter(
                                                (o) =>
                                                  o.value !== ex.exerciseId &&
                                                  !ex.swaps.find(
                                                    (s) => s.id === o.value,
                                                  ),
                                              )}
                                              value={null}
                                              onChange={(opt) => {
                                                if (!opt) return;
                                                handleAddSwap(
                                                  activeDay,
                                                  track,
                                                  block,
                                                  exIdx,
                                                  opt.value,
                                                  opt.label,
                                                );
                                                setSwapOptions([]);
                                              }}
                                              onInputChange={(val) => {
                                                searchSwapExercises(val);
                                              }}
                                              placeholder="Add swap exercise..."
                                              className="text-xs"
                                              controlShouldRenderValue={false}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    ))}

                                    <button
                                      onClick={() =>
                                        addExercise(activeDay, track, block)
                                      }
                                      className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-yellow-400 hover:text-yellow-600 transition-colors"
                                    >
                                      + Add Exercise to {blockLabel}
                                    </button>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Submit */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={handleSaveAsNew}
          disabled={submitting}
          className="px-8 py-3 bg-white text-yellow-600 border border-yellow-600 rounded-lg font-medium hover:bg-yellow-50 disabled:opacity-50"
        >
          {submitting ? "Saving..." : "Save as New"}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-8 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 disabled:opacity-50"
        >
          {submitting ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default EditTrainerProgramPage;
